/**
 * First-run checklist. Each step is done only when an existing engine read
 * proves it. A failed read stays unknown and does not count as empty.
 *
 * Verified on semanticad-engine main (read-only):
 * - GET /v1/auth/me — organization_id, organization_slug
 * - POST /v1/auth/login and /v1/auth/register — organization.name (session)
 * - GET /v1/erp/machines
 * - GET /v1/erp/stock
 * - GET /v1/rfqs
 * - GET /v1/jobs
 *
 * Not detectable, so not used:
 * - Shop legal identity (tax, address, logo). organizations.settings accepts
 *   only zilliz_enabled and pricing.
 * - Whether the shop form was saved. GET /v1/organization/settings always
 *   merges pricing defaults, so a never-saved org looks like the defaults.
 * - Machine, stock, RFQ, or job presence when that call fails (including
 *   erp_v2_disabled). The checklist stays hidden until every signal is known.
 */

export type OnboardingStepId = "account" | "machines" | "stock" | "drawing";

export type OnboardingSignals = {
  /** True when the org exists. Null when /v1/auth/me failed and no session names one. */
  account: boolean | null;
  /** Active machine rows from GET /v1/erp/machines. Null when that call failed. */
  machines: number | null;
  /** Stock rows from GET /v1/erp/stock. Null when that call failed. */
  stock: number | null;
  /** RFQs plus jobs. Null when neither list proved a row and one of them failed. */
  drawings: number | null;
};

export type OnboardingStep = {
  id: OnboardingStepId;
  done: boolean;
};

export type AccountIdentity = {
  organizationId?: string | null;
  organizationSlug?: string | null;
  organizationName?: string | null;
};

/** Signup stores organizations.name. /v1/auth/me returns the id and slug. */
export function accountReady(input: AccountIdentity | null | undefined): boolean {
  if (!input) return false;
  return [input.organizationId, input.organizationSlug, input.organizationName].some(
    (value) => typeof value === "string" && value.trim().length > 0,
  );
}

/**
 * A drawing or an RFQ completes the last step.
 * One successful empty list plus a failed list is not proof of zero.
 */
export function drawingPresence(rfqCount: number | null, jobCount: number | null): number | null {
  const rfqs = typeof rfqCount === "number" && rfqCount > 0 ? rfqCount : 0;
  const jobs = typeof jobCount === "number" && jobCount > 0 ? jobCount : 0;
  if (rfqs > 0 || jobs > 0) return rfqs + jobs;
  if (rfqCount === null || jobCount === null) return null;
  return 0;
}

export function onboardingSteps(signals: OnboardingSignals): OnboardingStep[] {
  return [
    { id: "account", done: signals.account === true },
    { id: "machines", done: (signals.machines ?? 0) > 0 },
    { id: "stock", done: (signals.stock ?? 0) > 0 },
    { id: "drawing", done: (signals.drawings ?? 0) > 0 },
  ];
}

export function onboardingSignalsKnown(signals: OnboardingSignals): boolean {
  return (
    signals.account !== null &&
    signals.machines !== null &&
    signals.stock !== null &&
    signals.drawings !== null
  );
}

/** The checklist replaces Home only when every signal loaded and one step is open. */
export function showOnboarding(signals: OnboardingSignals): boolean {
  if (!onboardingSignalsKnown(signals)) return false;
  return onboardingSteps(signals).some((step) => !step.done);
}

export function onboardingDoneCount(signals: OnboardingSignals): number {
  return onboardingSteps(signals).filter((step) => step.done).length;
}
